import {
  X,
  Bed as BedIcon,
  Building2,
  Tag,
  Activity,
  Banknote,
  FileText,
  MapPin,
  DoorOpen,
  Edit2,
  Calendar,
  Power,
  CheckCircle2,
  Clock,
  Wrench,
  Stethoscope,
  Users,
  Baby,
  HeartPulse,
  Zap,
} from 'lucide-react';
import { useWard } from '../../services/httpServices/queries';
import { formatOrdinalDateTimeLong } from '../../utils/dateFormat';

export interface BedDetails {
  id: string;
  bedNumber: string;
  ward: string;
  wardCode?: string;
  wardId?: string;
  type: string;
  status: string;
  price?: number;
  description?: string;
  isActive?: boolean;
  createdAt?: string;
}

interface BedDetailsModalProps {
  bed: BedDetails | null;
  onClose: () => void;
  onEdit?: (bed: BedDetails) => void;
}

const typeConfig: Record<string, { label: string; icon: React.ElementType }> = {
  GENERAL: { label: 'General', icon: BedIcon },
  SEMI_PRIVATE: { label: 'Semi-Private (Cabin)', icon: Users },
  PRIVATE: { label: 'Private (Cabin)', icon: BedIcon },
  ICU: { label: 'ICU', icon: Stethoscope },
  CCU: { label: 'CCU', icon: HeartPulse },
  NICU: { label: 'NICU', icon: Baby },
  EMERGENCY: { label: 'Emergency', icon: Zap },
};

const statusConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  AVAILABLE: { label: 'Available', icon: CheckCircle2, color: 'bg-success/10 text-success' },
  OCCUPIED: { label: 'Occupied', icon: BedIcon, color: 'bg-danger/10 text-danger' },
  RESERVED: { label: 'Reserved', icon: Clock, color: 'bg-warning/10 text-warning' },
  MAINTENANCE: { label: 'Maintenance', icon: Wrench, color: 'bg-text-muted/10 text-text-muted' },
};

function InfoTile({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  return (
    <div className="p-3 rounded-xl bg-background border border-border">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-text-muted mb-1">
        <Icon size={12} />
        {label}
      </div>
      <div className="text-sm font-medium text-text break-words">{value}</div>
    </div>
  );
}

export function BedDetailsModal({ bed, onClose, onEdit }: BedDetailsModalProps) {
  const { data: wardData } = useWard(bed?.wardId || '');
  const ward = wardData?.data;

  if (!bed) return null;

  const type = typeConfig[bed.type] || { label: bed.type || '—', icon: BedIcon };
  const TypeIcon = type.icon;
  const status = statusConfig[bed.status] || {
    label: bed.status || '—',
    icon: BedIcon,
    color: 'bg-text-muted/10 text-text-muted',
  };
  const StatusIcon = status.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <BedIcon size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-text truncate">{bed.bedNumber}</h2>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                  <StatusIcon size={12} />
                  {status.label}
                </span>
              </div>
              <p className="text-xs text-text-muted mt-0.5">{bed.ward || 'Unassigned ward'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-5 overflow-y-auto">
          {/* Core details */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <InfoTile icon={TypeIcon} label="Bed Type" value={type.label} />
            <InfoTile icon={StatusIcon} label="Status" value={status.label} />
            <InfoTile icon={Banknote} label="Daily Price" value={`৳${Number(bed.price || 0).toFixed(2)}`} />
            <InfoTile icon={Building2} label="Ward" value={bed.ward || '—'} />
            <InfoTile icon={Tag} label="Ward Code" value={bed.wardCode || ward?.wardCode || '—'} />
            <InfoTile
              icon={Power}
              label="State"
              value={
                <span className={bed.isActive === false ? 'text-text-muted' : 'text-success'}>
                  {bed.isActive === false ? 'Inactive' : 'Active'}
                </span>
              }
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-primary" />
              <h3 className="text-sm font-semibold text-text">Description</h3>
            </div>
            <p className="text-sm text-text-muted">
              {bed.description || 'No description provided for this bed.'}
            </p>
          </div>

          {/* Ward context */}
          {ward && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-primary" />
                <h3 className="text-sm font-semibold text-text">Ward Details</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <InfoTile icon={Activity} label="Ward Type" value={ward.wardType?.replace('_', ' ') || '—'} />
                <InfoTile icon={MapPin} label="Floor" value={ward.floor || '—'} />
                <InfoTile icon={DoorOpen} label="Rooms" value={ward.rooms?.length ?? 0} />
              </div>
              {ward.rooms && ward.rooms.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {ward.rooms.map((room: any) => (
                    <span
                      key={room.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-background border border-border text-xs text-text"
                    >
                      <DoorOpen size={12} className="text-text-muted" />
                      {room.name}
                      {room.floor?.name && <span className="text-text-muted">· {room.floor.name}</span>}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Meta */}
          {bed.createdAt && (
            <div className="flex items-center gap-2 text-xs text-text-muted pt-1 border-t border-border">
              <Calendar size={12} />
              Created {formatOrdinalDateTimeLong(bed.createdAt)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-text-muted hover:text-text hover:bg-background transition-colors"
          >
            Close
          </button>
          {onEdit && (
            <button
              onClick={() => onEdit(bed)}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors flex items-center gap-2"
            >
              <Edit2 size={16} />
              Edit Bed
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
